<template>
  <div class="hot-drama-container">
    <div class="container-title">
      为你推荐
    </div>
    <div
      ref="wrapperRef"
      class="drama-wrapper"
    >
      <div class="drama">
        <div
          v-for="(item, index) in dramasList"
          :key="item.id"
          ref="dramaItem"
          class="drama-item"
          @click.stop="clickDramaItem(item, index)"
        >
          <div :class="{ active: activeIndex === index }">
            <div
              class="cover"
              :style="{ backgroundImage: getImage(item?.coverImg) }"
            />
            <div class="feed-name">
              {{ item?.name }}
            </div>
            <div class="feed-desc">
              {{ item?.recmdDesc }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="share-bottom"
      @click="onClickBtn"
    >
      <div class="weishi-icon" />
      去腾讯微视看全集
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, watch, nextTick } from 'vue';
import BScroll, { BScrollInstance } from '@better-scroll/core';
import { useStore, MutationTypes } from '@/store/index';
import { fetchHotDramas, fetchDramaFeed } from '@/services/fetch-hot-dramas';
import { Drama } from '@/types/drama';
import { fixHttps } from '@/utils/no-import-meta/url';
import { formatFeed } from '@/utils/formatter';
import { changeShareMaskText, TEXT } from '@/utils/share';
import { EVideoEvtNameTypes } from '@/constants/video';
import useVideoEventHandler from '@/composable/use-video-event-handler';
import useGetCommonMethods from '@/composable/use-get-common-method';
import useProvideUrlInfo from '@/composable/use-provide-url-info';
import useProvideBrowserInfo from '@/composable/use-provide-browser-info';
import { useDramaReport } from '@/composable/use-compoent-report-methods';

const { openSchemaAppendParamsClipboard, getLogsour } = useGetCommonMethods();
const { emitVideoEvent } = useVideoEventHandler();
const { info: urlInfo } = useProvideUrlInfo();
const { info: browserInfo } = useProvideBrowserInfo();
const dramaReport = useDramaReport();

const store = useStore();
const { state } = store;
const activeIndex = computed(() => state.activeIndex);
const feedsList = computed(() => state.feedsList);
const dramaList = computed(() => state.dramaList || []);
const shareUi = computed(() => state.shareUi);

const dramasList = ref<Drama[]>([]);
const scroll = ref<null | BScrollInstance>(null);
const wrapperRef = ref<null | HTMLElement>(null);
const dramaItems = ref<HTMLElement[]>([]);
const indexMap = ref<Record<number, number>>({});

const getImage = (url: string) => `url(${fixHttps(url)})`;

onMounted(async () => {
  const dramaRes = await fetchHotDramas();
  dramasList.value = dramaRes?.dramas.filter((_, index) => index < 6) || [];

  dramasList.value.map((drama, index) => dramaReport.mountedHotDramaReport(drama, index));
});

const scrollToElement = (tempIndex: number, speed = 300) => {
  if (!scroll.value) return;
  const index = Math.max(tempIndex, 1);
  scroll.value.scrollToElement(dramaItems.value[index - 1], speed, false, false);
};

watch(dramasList, async (val) => {
  if (!wrapperRef.value || !val.length) return;
  // 缓存 dom 节点
  await nextTick();
  dramaItems.value = wrapperRef.value.querySelectorAll('.drama-item') as unknown as HTMLElement[];
  scroll.value = new BScroll(wrapperRef.value, { click: true, scrollX: true });
  if (activeIndex.value >= 0) scrollToElement(activeIndex.value, 0);
});

const clickDramaItem = async (item: Drama, index: number) => {
  scrollToElement(index);
  let curIndex;
  if (indexMap.value[index]) {
    curIndex = indexMap.value[index];
  } else {
    const { dramaFeeds, drama } = await fetchDramaFeed({
      dramaID: item.id,
    });

    curIndex = feedsList.value.length;
    if (feedsList.value) {
      feedsList.value.push(formatFeed(dramaFeeds[0].feed, curIndex));
      indexMap.value[index] = curIndex;

      dramaList.value.push(drama);
      store.commit(MutationTypes.SET_DRAMA_LIST, { value: dramaList.value });
    }
  }
  dramaReport.hotDramaClick(item, index, feedsList.value[curIndex]);

  let eventName = EVideoEvtNameTypes.readyPlay;
  // 重复点击相同的视频卡片，视频需要播放暂停切换
  if (activeIndex.value === curIndex) {
    eventName = EVideoEvtNameTypes.changePlayOrPause;
  } else {
    // 当切换视频，新视频需要启动播放
    eventName = EVideoEvtNameTypes.play;
  }

  store.commit(MutationTypes.SET_ACTIVE_INDEX, {
    value: curIndex,
  });

  setTimeout(() => {
    emitVideoEvent({
      index: curIndex,
      eventName: EVideoEvtNameTypes.play,
    });
  }, 200);
};

const onClickBtn = () => {
  dramaReport.shareBtnClick();

  const { channel } = urlInfo?.query ?? {};
  if (!channel && browserInfo.client === 'wx') {
    // 代表复制链接过来的且处于微信中
    changeShareMaskText(TEXT.OPEN);
    shareUi.value?.open();
    return;
  }

  const activeFeed = feedsList.value[activeIndex.value];
  const logsour = getLogsour();
  openSchemaAppendParamsClipboard(true, `weishi://feed?feed_id=${activeFeed.id}&session_from=10000018&logsour=${logsour}`);
};

</script>

<style lang="scss">
.hot-drama-container {
  @include responsiveStyleSingle(padding-top, 24, 'px');
  @include responsiveStyleSingle(padding-bottom, 50, 'px');
  padding-right: 0;
  background: #1F1F23;
  color: #fff;
  width: 100%;
  flex-shrink: 0;
  z-index: 999;
  margin-top: auto;

  .container-title {
    @include responsiveStyleSingle(font-size, 32, 'px');
    @include responsiveStyleSingle(margin-bottom, 22, 'px');
    @include responsiveStyleSingle(margin-left, 24, 'px');
    font-weight: 500;
  }

  .drama-wrapper {
    @include responsiveStyleSingle(margin-bottom, 54, 'px');
    @include responsiveStyleSingle(margin-left, 24, 'px');

    .drama {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      @include responsiveStyleSingle(height, 382, 'px');

      .drama-item {
        box-sizing: border-box;
        @include responsiveStyleSingle(width, 208, 'px');
        @include responsiveStyleSingle(margin-right, 15, 'px');

        .cover {
          @include responsiveStyleSingle(width, 208, 'px');
          @include responsiveStyleSingle(height, 290, 'px');
          @include responsiveStyleSingle(border-radius, 15, 'px');
          background-size: cover;
          background-position: center center;
        }

        .feed-name {
          @include responsiveStyleSingle(font-size, 28, 'px');
          @include responsiveStyleSingle(margin-top, 12, 'px');
          @include responsiveStyleSingle(margin-bottom, 4, 'px');
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .feed-desc {
          @include responsiveStyleSingle(font-size, 26, 'px');
          color: #696969;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .share-bottom {
    @include responsiveStyleSingle(width, 612, 'px');
    @include responsiveStyleSingle(height, 88, 'px');
    @include responsiveStyleSingle(font-size, 32, 'px');
    @include responsiveStyleSingle(border-radius, 44, 'px');

    background: #7642F5;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto;

    .weishi-icon {
      @include responsiveStyleSingle(width, 30, 'px');
      @include responsiveStyleSingle(height, 35, 'px');
      @include responsiveStyleSingle(margin-right, 5, 'px');
      background: url('https://isee.weishi.qq.com/share/weishi-logo.png') no-repeat;
      background-size: 100% auto;
    }
  }
}
</style>
